import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { locationsApi, LocationsResponse } from "../api/locations";

/**
 * Query keys for location-related queries
 * Centralized to ensure consistency across the app
 */
export const LOCATIONS_QUERY_KEYS = {
  all: ["locations"] as const,
  list: () => ["locations", "list"] as const,
};

/**
 * Hook to fetch all locations
 * Uses React Query for caching and automatic refetching
 */
export const useLocations = (
  options?: UseQueryOptions<LocationsResponse, Error>
) => {
  return useQuery<LocationsResponse, Error>({
    queryKey: LOCATIONS_QUERY_KEYS.list(),
    queryFn: () => locationsApi.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    ...options,
  });
};

/**
 * Helper hook to get location options for dropdowns
 * Returns a simplified list of { value, label } objects
 */
export const useLocationOptions = () => {
  const { data, isLoading, error } = useLocations();

  const options = data?.value.map((location) => ({
    value: location.code,
    label: location.displayName,
    id: location.id,
  })) || [];

  return {
    options,
    isLoading,
    error,
  };
};
